import { array, nonEmptyArray, option } from "fp-ts";
import { P, match } from "ts-pattern";
import { getPercentagePointsFromString } from "./graph";
import { BigMath } from "./bigint";
import {
  makePercent,
  Percent,
  PERCENT_FACTOR,
  PERCENT_ONE_HUNDRED,
} from "@silo/types";
import { BigInt, Option, pipe } from "effect";

export const VOLATILE_THRESHOLD = getPercentagePointsFromString("15").value;
export const STABLE_THRESHOLD = getPercentagePointsFromString("3").value;
const STABLE_LT_CUTOFF = getPercentagePointsFromString("90").value;

export const getLoanNotificationThreshold = (liquidationThresholds: bigint[]) =>
  pipe(
    liquidationThresholds,
    array.map((liquidationThreshold) =>
      match(liquidationThreshold)
        .with(P.bigint.gte(STABLE_LT_CUTOFF), () => STABLE_THRESHOLD)
        .otherwise(() => VOLATILE_THRESHOLD)
    ),
    nonEmptyArray.fromArray,
    option.map((thresholds) => BigMath.max(0n, ...thresholds)),
    option.getOrElse(() => VOLATILE_THRESHOLD)
  );

export const getIsAtRiskOfLiquidation = (
  healthFactor: bigint,
  liquidationThresholds: bigint[]
) =>
  pipe(
    liquidationThresholds,
    nonEmptyArray.fromArray,
    option.map(getLoanNotificationThreshold),
    option.map((loanThreshold) => healthFactor <= loanThreshold),
    option.getOrElse(() => false)
  );

export function getHealthFactor(
  userLtv: Percent,
  userLiquidationThreshold: Percent
): Percent {
  const riskFactor = pipe(
    userLtv * PERCENT_FACTOR,
    BigInt.divide(userLiquidationThreshold),
    Option.getOrElse(() => 0n)
  );

  const healthFactor = pipe(PERCENT_ONE_HUNDRED - riskFactor, makePercent);

  return healthFactor;
}

export function getBorrowingPowerUsed(
  userLtv: Percent,
  userMaxLtv: Percent
): Percent {
  return pipe(
    userLtv * PERCENT_FACTOR,
    BigInt.divide(userMaxLtv),
    Option.getOrElse(() => 0n),
    makePercent
  );
}
